<template>
  <div class="grading-report-summary">
    <section-title size="24px">
      <div class="header">
        <div>
          {{ selectedReport.caseName || caseConf.name }} -
          <span v-if="selectedReport.user">{{ selectedReport.user.given_name }} {{ selectedReport.user.family_name }}</span>
          <span v-if="!selectedReport.user">User not found</span>
          <span v-if="selectedReport.displayType && selectedReport.displayType !== 'UNKNOWN'">
            - {{ displayTypeLabel(selectedReport.displayType) }}
          </span>
        </div>

        <md-menu>
          <md-button class="md-icon-button" md-menu-trigger>
            <md-icon>share</md-icon>
          </md-button>
          <md-menu-content>
            <md-menu-item target="_blank" :href="gradingReportPdfUrl">Export to PDF of Grades Only</md-menu-item>
            <md-menu-item target="_blank" :href="gradingReportCsvUrl">Export to CSV of Grades Only</md-menu-item>
            <md-divider />
            <md-menu-item target="_blank" :href="gradingReportsSummaryPdfUrl">Export to PDF of Users Only</md-menu-item>
            <md-menu-item target="_blank" :href="gradingReportsSummaryCsvUrl">Export to CSV of Users Only</md-menu-item>
            <md-divider />
            <md-menu-item target="_blank" :href="gradingReportsBulkCsvUrl">Export to CSV of Grades - All Users</md-menu-item>
          </md-menu-content>
        </md-menu>
      </div>
    </section-title>
    <div v-if="!selectedReport.isStatusOnly" class="grade-container">
      <div>
        <div
          class="success-status"
          :class="{successful: selectedReport.successful, failed: !selectedReport.successful}"
        >
          {{ `${selectedReport.successful ? 'SUCCESS' : 'FAILURE' }${selectedReport.exitEarly ? ' - INCOMPLETE' : ''}` }}
        </div>
        <div class="grade">{{ selectedReport.grade }}/{{ totalPoints }} Points</div>
      </div>
      <div
        class="percentage"
        :class="{successful: selectedReport.successful, failed: !selectedReport.successful}"
      >
        {{ percentage }} %
      </div>
    </div>
    <div class="filters-container">
      <md-field class="filter-field">
        <md-select v-model="state.filter" placeholder="Filters">
          <md-option>All</md-option>
          <md-option value="CRITICAL">Critical</md-option>
          <md-option value="NON_CRITICAL">Non Critical</md-option>
          <md-option value="QUESTION">Questions</md-option>
          <md-option value="IV">IV</md-option>
          <md-option value="MEDICATION">Medications</md-option>
          <md-option value="OXYGEN">Oxygen</md-option>
          <md-option value="PROCEDURE">Procedures</md-option>
          <md-option value="MONITOR">Monitors</md-option>
        </md-select>
      </md-field>
      <md-field class="detail-field">
        <md-select v-model="state.selectedReportId" placeholder="History">
          <md-option
            v-for="reportItem in report.history"
            :key="reportItem._id"
            :value="reportItem._id"
          >
            {{ formatReportDate(reportItem) }}
          </md-option>
        </md-select>
      </md-field>
    </div>
    <slick :options="slickOptions" class="detail-container">
      <div class="scored-items md-scrollbar md-theme-default">
        <h3>SCORED ITEMS</h3>
        <div
          v-for="action in filteredActions"
          :key="action._id"
          class="grading-item"
        >
          <div class="item-description">
            <div class="category">{{ categoryLabel(action) }}</div>
            <div class="label">{{ actionLabel(action) || action.label || action.typeID }}</div>
            <div
              class="item-grade"
              :class="{need: action.grade === 'NEED', avoid: action.grade === 'AVOID'}"
            >
              {{ action.grade }} {{ action.isCritical ? '- CRITICAL' : '' }}
            </div>
          </div>
          <div class="item-points-taken">
            <div class="points">{{ action.points }}pts</div>
            <md-icon
              class="item-success-status"
              :class="{successful: action.successful, failed: !action.successful}"
            >
              {{ action.successful ? 'done' : 'clear' }}
            </md-icon>
          </div>
        </div>
      </div>
      <div class="grading-report-timeline-container">
        <grading-report-timeline v-if="selectedReport.timeline" :report="selectedReport" :timeline="selectedReport.timeline" />
        <md-empty-state
          v-else
          class="md-primary"
          md-icon="info"
          md-label="No timeline"
          md-description="No timeline available for this report"
        />
      </div>
      <template v-if="isAiAvailable">
        <grading-report-transcript :report="selectedReport" />
      </template>
      <template v-if="isAiAvailable">
        <grading-report-ai-feedback :report="selectedReport" />
      </template>
    </slick>
  </div>
</template>

<script>
import sortBy from 'lodash/sortBy';
import keyBy from 'lodash/keyBy';
import { DateTime } from 'luxon';
import Slick from 'vue-slick';

import SectionTitle from '../common/SectionTitle';
import { actionLabel, categoryLabel } from '~lib/common/utils/gradingReports';
import { displayTypeLabel } from '~lib/common/utils/labels';

import GradingReportTimeline from './GradingReportTimeline.vue';
import GradingReportTranscript from './GradingReportTranscript.vue';
import GradingReportAiFeedback from './GradingReportAiFeedback.vue';

const actionTypeOrderMap = {
  QUESTION: 0,
  MEDICATION: 1,
  MEDICATION_ROUTE: 2,
  IV_SITE: 3,
  IV_GAUGE: 4,
  IV_SET: 5,
  IV_FLUID: 6,
  IV_FLUID_RATE: 7,
  IV_VOLUME: 8,
  IV_TUBING: 9,
  IV_SALINE_LOCK: 10,
  OXYGEN_ROUTE: 11,
  PROCEDURE: 12,
  LEAD_SELECT: 13,
  SYNC: 14,
  DEFIBRILLATION: 15,
  PACING: 16,
};

const localDateTime = DateTime.local();
const browserTimeZone = localDateTime.zoneName;

export default {
  name: 'grading-report-summary',
  components: {
    SectionTitle,
    GradingReportTimeline,
    GradingReportTranscript,
    GradingReportAiFeedback,
    Slick,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    assignment: {
      type: Object,
      default: () => null,
    },
    user: {
      type: Object,
      default: () => null,
    },
    report: {
      type: Object,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
    isAiAvailable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      slickOptions: {
        slidesToShow: 1,
        arrows: true,
        dots: true,
        initialSlide: 0,
        draggable: false,
        swipe: false
      },
      state: {
        filter: null,
        selectedReportId: this.report._id,
      },
    };
  },
  computed: {
    historyReportsById() {
      return keyBy(this.report.history, '_id');
    },
    selectedReport() {
      return this.historyReportsById[this.state.selectedReportId];
    },
    totalPoints() {
      const report = this.selectedReport;
      return [
        ...report.successfullyTakenActions,
        ...report.successfullyAvoidedActions,
        ...report.failedToTakeActions,
        ...report.failedToAvoidActions,
      ].reduce((sum, action) => sum + action.points, 0);
    },
    percentage() {
      const report = this.selectedReport;
      return Math.round((report.grade / this.totalPoints) * 100);
    },
    allActions() {
      const report = this.selectedReport;
      return sortBy(
        [
          ...report.successfullyTakenActions.map(a => ({
            ...a,
            grade: 'NEED',
            successful: true,
          })),
          ...report.successfullyAvoidedActions.map(a => ({
            ...a,
            grade: 'AVOID',
            successful: true,
          })),
          ...report.failedToTakeActions.map(a => ({
            ...a,
            grade: 'NEED',
            successful: false,
          })),
          ...report.failedToAvoidActions.map(a => ({
            ...a,
            grade: 'AVOID',
            successful: false,
          })),
        ],
        action => actionTypeOrderMap[action.type],
      );
    },
    filteredActions() {
      const { filter } = this.state;
      if (!filter) {
        return this.allActions;
      }
      if (filter === 'CRITICAL') {
        return this.allActions.filter(a => a.isCritical);
      }
      if (filter === 'NON_CRITICAL') {
        return this.allActions.filter(a => !a.isCritical);
      }
      if (filter === 'QUESTION') {
        return this.allActions.filter(a => a.type === 'QUESTION');
      }
      if (filter === 'IV') {
        return this.allActions.filter(a =>
          [
            'IV_SITE',
            'IV_GAUGE',
            'IV_SET',
            'IV_FLUID',
            'IV_FLUID_RATE',
            'IV_VOLUME',
            'IV_TUBING',
            'IV_SALINE_LOCK',
          ].includes(a.type),
        );
      }
      if (filter === 'MEDICATION') {
        return this.allActions.filter(a =>
          ['MEDICATION', 'MEDICATION_ROUTE'].includes(a.type),
        );
      }
      if (filter === 'OXYGEN') {
        return this.allActions.filter(a => a.type === 'OXYGEN_ROUTE');
      }
      if (filter === 'PROCEDURE') {
        return this.allActions.filter(a => a.type === 'PROCEDURE');
      }
      if (filter === 'MONITOR') {
        return this.allActions.filter(a =>
          ['LEAD_SELECT', 'SYNC', 'DEFIBRILLATION', 'PACING'].includes(a.type),
        );
      }
      return this.allActions;
    },
    gradingReportPdfUrl() {
      return `${process.env.VUE_APP_GRADING_SERVICE_URL}/reports/${this.selectedReport._id}/pdf?caseName=${encodeURIComponent(this.caseConf.name)}&token=${this.token}&timeZone=${browserTimeZone}`;
    },
    gradingReportCsvUrl() {
      return `${process.env.VUE_APP_GRADING_SERVICE_URL}/reports/${this.selectedReport._id}/csv?caseName=${encodeURIComponent(this.caseConf.name)}&token=${this.token}&timeZone=${browserTimeZone}`;
    },
    gradingReportsSummaryPdfUrl() {
      return `${process.env.VUE_APP_GRADING_SERVICE_URL}/reports/pdf?filter=${encodeURIComponent(JSON.stringify({ caseId: this.caseConf.id, assignmentId: this.assignment ? this.assignment.id : undefined, userId: this.user ? this.user.user_id : undefined }))}&caseName=${encodeURIComponent(this.caseConf.name)}&token=${this.token}&latestOnly=true&timeZone=${browserTimeZone}`;
    },
    gradingReportsSummaryCsvUrl() {
      return `${process.env.VUE_APP_GRADING_SERVICE_URL}/reports/csv?filter=${encodeURIComponent(JSON.stringify({ caseId: this.caseConf.id, assignmentId: this.assignment ? this.assignment.id : undefined, userId: this.user ? this.user.user_id : undefined }))}&caseName=${encodeURIComponent(this.caseConf.name)}&token=${this.token}&latestOnly=true&timeZone=${browserTimeZone}`;
    },
    gradingReportsBulkCsvUrl() {
      return `${process.env.VUE_APP_GRADING_SERVICE_URL}/reports/bulk/csv?filter=${encodeURIComponent(JSON.stringify({ caseId: this.caseConf.id, assignmentId: this.assignment ? this.assignment.id : undefined, userId: this.user ? this.user.user_id : undefined }))}&caseName=${encodeURIComponent(this.caseConf.name)}&token=${this.token}&latestOnly=true&timeZone=${browserTimeZone}`;
    },
  },
  watch: {
    report(newReport) {
      this.state.selectedReportId = newReport._id;
    },
  },
  methods: {
    actionLabel,
    categoryLabel,
    displayTypeLabel,
    formatReportDate(report) {
      return DateTime.fromISO(report.attemptDate).toFormat('MMMM d, yyyy h:mm a');
    },
  },
};
</script>

<style scoped>
.header {
  display: flex;
  align-items: center;
}
.grading-report-summary {
  padding: 20px 45px;
  color: #000;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.grade-container {
  display: flex;
  justify-content: space-between;
  padding: 16px 0;
}

.success-status {
  font-size: 36px;
  line-height: 36px;
  font-weight: 500;
  margin-bottom: 6px;
}

.success-status.successful {
  color: #62cd63;
}

.success-status.failed {
  color: #db4437;
}

.grade {
  font-size: 26px;
  color: #303243;
  font-weight: 500;
}

.percentage {
  display: flex;
  white-space: nowrap;
  align-items: center;
  font-size: 28px;
  line-height: 28px;
  border: solid 4px;
  border-radius: 8px;
  padding: 7px 15px;
  font-weight: bold;
}

.percentage.successful {
  color: #62cd63;
  border-color: #62cd63;
}
.percentage.failed {
  color: #db4437;
  border-color: #db4437;
}

.filters-container {
  margin-top: 24px;
  margin-bottom: 24px;
  display: flex;
  flex-direction: row;
}

.filters-container .md-field {
  margin-top: 0;
}

.filter-field {
  width: 35%;
}

.detail-field {
  width: 65%;
}

.detail-container {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.detail-container >>> .slick-list,
.detail-container >>> .slick-slide,
.detail-container >>> .slick-slide > div {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.detail-container >>> .slick-slide > div .grading-report-ai-feedback {
  user-select: text !important;
}

.detail-container >>> .slick-track {
  display: flex;
  flex-direction: row;
  min-height: 0;
}

.detail-container >>> .slick-slider.slick-dotted {
  height: 94%;
}

.detail-container >>> .slick-dots {
  height: auto;
}

.detail-container >>> .slick-dots button::before {
  font-size: 16px;
}

.detail-container >>> .slick-arrow::before {
  color: rgba(0, 0, 0, 0.54);
}

.scored-items {
  height: 100%;
  overflow: auto;
  /* Scrollbar is bleeding through timeline in slides */
  width: 99% !important;
}

.grading-report-timeline-container {
  height: 100%;
}

.grading-item {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  font-weight: 500;
  font-size: 14px;
  padding: 5px 0;
}

.grading-item:nth-child(even) {
  background-color: #ededed;
}

.item-description {
  padding: 0 5px;
}

.item-description .label {
  display: block; /* or inline-block */
  margin: 0;
  text-overflow: ellipsis;
  word-wrap: break-word;
  overflow: hidden;
  max-height: 1.4em;
  line-height: 1.4em;
}

.item-grade {
  font-size: 16px;
  font-weight: bold;
}

.item-grade.need {
  color: #62cd63;
}

.item-grade.avoid {
  color: #db4437;
}

.item-points-taken {
  display: flex;
  align-items: center;
  font-size: 20px;
  border-left: solid 1.5px #000;
  margin: 10px 0;
  padding: 0 12px;
}

.item-points-taken .points {
  font-weight: bold;
}

.item-success-status {
  border: solid 4px;
  border-radius: 8px;
  font-size: 48px !important;
  width: 48px !important;
  height: 48px !important;
  margin-left: 12px;
}

.item-success-status.successful {
  color: #62cd63;
  border-color: #62cd63;
}

.item-success-status.failed {
  color: #db4437;
  border-color: #db4437;
}
</style>